import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  confirmPatientMfaEnrollment,
  startPatientMfaEnrollment,
} from '../../api/patientPortal/auth.ts';
import { patientPortalErrorMessage } from '../../api/patientPortal/errors.ts';
import { Button, DetailRow, ErrorText, Field, Panel } from '../../components/primitives.tsx';
import { PortalHelpLink } from '../help/PortalHelpLink.tsx';
import { usePatientSession } from '../session/PatientSessionContext.ts';

/**
 * Account settings — identity plus optional TOTP MFA enrollment (`docs/decisions/patient-portal-
 * scope.md`'s "MFA" section: available, reusing the existing TOTP infrastructure, not mandatory for
 * v1). This is the only place a patient can turn MFA on, and doing so only works end-to-end because
 * `PortalLoginPage.tsx` already handles the resulting `mfa_required` step on the next sign-in — see
 * that page's own header.
 */
export function PortalAccountPage() {
  const { t } = useTranslation('portalAccount');
  const session = usePatientSession();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <h1 className="m-0 text-xl font-semibold text-ink">{t('heading')}</h1>
        <PortalHelpLink topicId="your-account-settings" />
      </div>

      <Panel title={t('identity.panelTitle')}>
        <dl className="m-0">
          <DetailRow label={t('identity.name')}>
            {session.firstName} {session.lastName}
          </DetailRow>
          <DetailRow label={t('identity.email')}>{session.email}</DetailRow>
        </dl>
      </Panel>

      <MfaPanel />
    </div>
  );
}

type MfaStep = 'idle' | 'enrolling' | 'confirmed';

function MfaPanel() {
  const { t } = useTranslation('portalAccount');
  const [step, setStep] = useState<MfaStep>('idle');
  const [secret, setSecret] = useState<string | null>(null);
  const [provisioningUri, setProvisioningUri] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<readonly string[] | null>(null);

  const enroll = useMutation({
    mutationFn: startPatientMfaEnrollment,
    onSuccess: (response) => {
      setSecret(response.secret);
      setProvisioningUri(response.provisioningUri);
      setStep('enrolling');
    },
  });

  const confirm = useMutation({
    mutationFn: confirmPatientMfaEnrollment,
    onSuccess: (codes) => {
      setRecoveryCodes(codes);
      setStep('confirmed');
    },
  });

  // One title, reused across all three steps below, rather than three copies of the same
  // `t('mfa.panelTitle')` + `PortalHelpLink` pairing (`claude-kit/rules/invariants.md`'s "nothing is
  // written twice" applies to markup here just as much as to the explanation text itself).
  const panelTitle = (
    <>
      {t('mfa.panelTitle')}
      <PortalHelpLink topicId="two-factor-sign-in" />
    </>
  );

  if (step === 'confirmed') {
    return (
      <Panel title={panelTitle}>
        <p className="m-0 mb-3 text-sm text-ink">{t('mfa.enabledBody')}</p>
        <p className="m-0 mb-2 text-sm font-medium text-ink">{t('mfa.recoveryCodesHeading')}</p>
        <ul className="m-0 grid grid-cols-2 gap-1 rounded-control border border-line bg-field p-3 font-mono text-sm text-ink">
          {(recoveryCodes ?? []).map((recoveryCode) => (
            <li key={recoveryCode}>{recoveryCode}</li>
          ))}
        </ul>
        <p className="m-0 mt-3 text-sm text-ink-muted">{t('mfa.recoveryCodesHint')}</p>
      </Panel>
    );
  }

  if (step === 'enrolling') {
    return (
      <Panel title={panelTitle}>
        <p className="m-0 mb-3 text-sm text-ink">{t('mfa.enrollingBody')}</p>
        {secret !== null ? (
          <p className="m-0 mb-3 rounded-control border border-line bg-field p-3 font-mono text-sm break-all text-ink">
            {secret}
          </p>
        ) : null}
        {provisioningUri !== null ? (
          <p className="m-0 mb-3 text-xs break-all text-ink-muted">{provisioningUri}</p>
        ) : null}

        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            confirm.mutate({ code });
          }}
        >
          <Field
            label={t('mfa.codeLabel')}
            required
            inputMode="numeric"
            pattern="\d{6}"
            maxLength={6}
            value={code}
            onChange={(event) => {
              setCode(event.target.value);
            }}
          />
          {confirm.isError ? (
            <ErrorText message={patientPortalErrorMessage(confirm.error)} />
          ) : null}
          <Button type="submit" pending={confirm.isPending} className="self-start">
            {confirm.isPending ? t('mfa.confirming') : t('mfa.confirm')}
          </Button>
        </form>
      </Panel>
    );
  }

  return (
    <Panel title={panelTitle}>
      <p className="m-0 mb-3 text-sm text-ink">{t('mfa.idleBody')}</p>
      {enroll.isError ? <ErrorText message={patientPortalErrorMessage(enroll.error)} /> : null}
      <Button
        pending={enroll.isPending}
        onClick={() => {
          enroll.mutate();
        }}
      >
        {enroll.isPending ? t('mfa.starting') : t('mfa.start')}
      </Button>
    </Panel>
  );
}
